import { type FormEvent, useId, useRef, useState } from "react";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { PrivacyCheckbox } from "@/components/legal/PrivacyCheckbox";
import { Button } from "@/components/ui/Button";
import { PageHero } from "@/components/ui/PageHero";
import { ADVISORY_FORM_PATH, SOCIAL } from "@/config/constants";
import {
  ADVISORY_FORM_INTRO,
  ADVISORY_FORM_UI,
  ADVISORY_SECTIONS,
  type AdvisoryField,
  formText,
} from "@/data/advisoryForm";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLocale } from "@/i18n/useLocale";
import { trackEvent } from "@/utils/analytics";
import { type CalendlyPrefill, openCalendlyPopup } from "@/utils/calendly";
import { submitAdvisoryForm } from "@/utils/submitAdvisoryForm";

const inputCls =
  "w-full rounded-xl border border-brand-primary/15 bg-white px-4 py-3 text-base text-ink outline-none transition-colors focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40 aria-[invalid=true]:border-red-700";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status = "editing" | "submitting" | "success" | "error";

/** Error key for a field, or null when the answer is acceptable. */
function fieldError(field: AdvisoryField, value: string): keyof typeof ADVISORY_FORM_UI | null {
  const v = value.trim();
  if (field.id === "name" && v.length < 2) return "nameError";
  if (field.inputType === "email" && (!EMAIL_RE.test(v) || v.length > 254)) return "emailError";
  if (field.required && !v) return "requiredError";
  return null;
}

/**
 * Intake for the Asesoría Estratégica: the answers go to Jenifer's existing
 * Google Form, then the calendar opens pre-filled with name and email.
 */
export function AdvisoryFormPage() {
  usePageSeo(ADVISORY_FORM_PATH);
  const locale = useLocale();
  const ui = (key: keyof typeof ADVISORY_FORM_UI) => formText(ADVISORY_FORM_UI[key], locale);
  const formId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const topRef = useRef<HTMLParagraphElement>(null);

  const [step, setStep] = useState(0);
  /* The assistant may already know the visitor's name and email. */
  const carried = (useLocation().state as { prefill?: CalendlyPrefill } | null)?.prefill;
  const [answers, setAnswers] = useState<Record<string, string>>(() => ({
    name: carried?.name ?? "",
    email: carried?.email ?? "",
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [privacy, setPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);
  const [status, setStatus] = useState<Status>("editing");

  const total = ADVISORY_SECTIONS.length;
  const section = ADVISORY_SECTIONS[step];
  const last = step === total - 1;

  const prefill = { name: answers.name?.trim(), email: answers.email?.trim() };
  const openCalendar = () => {
    trackEvent("calendly_click", { location: "advisory_form" });
    void openCalendlyPopup(SOCIAL.calendlyAudit, prefill);
  };

  function setAnswer(id: string, value: string) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  }

  function validateStep(): boolean {
    const next: Record<string, string> = {};
    for (const field of section.fields) {
      const key = fieldError(field, answers[field.id] ?? "");
      if (key) next[field.id] = ui(key);
    }
    setErrors(next);
    const firstInvalid = section.fields.find((f) => next[f.id]);
    if (firstInvalid) {
      document.getElementById(`${formId}-${firstInvalid.id}`)?.focus();
      return false;
    }
    return true;
  }

  function goTo(nextStep: number) {
    setStep(nextStep);
    setErrors({});
    requestAnimationFrame(() => {
      headingRef.current?.focus({ preventScroll: true });
      topRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validateStep()) return;
    if (!last) {
      goTo(step + 1);
      return;
    }
    if (!privacy) {
      setPrivacyError(true);
      return;
    }
    setStatus("submitting");
    const entries: Record<number, string> = {};
    for (const s of ADVISORY_SECTIONS) {
      for (const field of s.fields) entries[field.entry] = answers[field.id] ?? "";
    }
    try {
      await submitAdvisoryForm(entries);
      trackEvent("advisory_form_submit", { location: "advisory_form" });
      setStatus("success");
      openCalendar();
    } catch {
      setStatus("error");
    }
  }

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={ui("crumb")}
        trail={[{ label: locale === "en" ? "Instagram advisory" : "Asesoría de Instagram", to: "/asesoria-instagram" }]}
        title={ui("title")}
        lead={formText(ADVISORY_FORM_INTRO, locale)}
      />

      <section aria-labelledby={`${formId}-heading`} className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 md:py-24">
          {status === "success" ? (
            <div role="status" className="rounded-[1.75rem] bg-teal-soft p-8 text-center sm:p-12">
              <CheckCircle2 aria-hidden="true" className="mx-auto size-10 text-teal-strong" />
              <h2
                id={`${formId}-heading`}
                className="mt-5 font-display text-3xl font-semibold text-brand-primary"
              >
                {ui("successTitle")}
              </h2>
              <p className="mx-auto mt-3 max-w-xl leading-relaxed text-ink-soft">{ui("successBody")}</p>
              <Button
                variant="primary"
                size="lg"
                arrow
                className="mt-8"
                icon={<CalendarCheck aria-hidden="true" className="size-4 shrink-0" />}
                aria-haspopup="dialog"
                onClick={openCalendar}
              >
                {ui("successAction")}
              </Button>
            </div>
          ) : (
            <form noValidate onSubmit={handleSubmit}>
              <p ref={topRef} className="scroll-mt-36 text-sm font-semibold uppercase tracking-[0.14em] text-teal-strong">
                {ui("step").replace("{current}", String(step + 1)).replace("{total}", String(total))}
              </p>
              <div
                role="progressbar"
                aria-label={ui("progressLabel")}
                aria-valuemin={1}
                aria-valuemax={total}
                aria-valuenow={step + 1}
                className="mt-3 h-1.5 overflow-hidden rounded-full bg-brand-primary/10"
              >
                <div
                  className="h-full rounded-full bg-teal-strong transition-[width] duration-500"
                  style={{ width: `${((step + 1) / total) * 100}%` }}
                />
              </div>

              <h2
                id={`${formId}-heading`}
                ref={headingRef}
                tabIndex={-1}
                className="mt-8 font-display text-3xl font-semibold text-brand-primary outline-none sm:text-4xl"
              >
                {formText(section.title, locale)}
              </h2>
              {section.description ? (
                <p className="mt-3 leading-relaxed text-ink-soft">{formText(section.description, locale)}</p>
              ) : null}

              <div className="mt-8 space-y-8">
                {section.fields.map((field) => (
                  <Field
                    key={field.id}
                    id={`${formId}-${field.id}`}
                    field={field}
                    locale={locale}
                    value={answers[field.id] ?? ""}
                    error={errors[field.id]}
                    requiredMark={ui("requiredMark")}
                    onChange={(v) => setAnswer(field.id, v)}
                  />
                ))}
              </div>

              {last ? (
                <div className="mt-10">
                  <PrivacyCheckbox
                    checked={privacy}
                    onChange={(v) => {
                      setPrivacy(v);
                      if (v) setPrivacyError(false);
                    }}
                    disabled={status === "submitting"}
                  />
                  {privacyError ? (
                    <p role="alert" className="mt-2 text-sm font-medium text-red-700">
                      {ui("privacyError")}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {status === "error" ? (
                <p role="alert" className="mt-6 text-sm font-medium text-red-700">
                  {ui("submitError")}
                </p>
              ) : null}

              <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                {step > 0 ? (
                  <Button variant="outline" size="md" onClick={() => goTo(step - 1)} disabled={status === "submitting"}>
                    {ui("back")}
                  </Button>
                ) : (
                  <span />
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  arrow
                  disabled={status === "submitting"}
                  aria-busy={status === "submitting"}
                >
                  {last ? ui("submit") : ui("next")}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({
  id,
  field,
  locale,
  value,
  error,
  requiredMark,
  onChange,
}: {
  id: string;
  field: AdvisoryField;
  locale: "es" | "en";
  value: string;
  error?: string;
  requiredMark: string;
  onChange: (value: string) => void;
}) {
  const label = formText(field.label, locale);
  const errorId = `${id}-error`;
  const marker = field.required ? (
    <span className="ml-1 text-teal-strong" aria-hidden="true">
      *
    </span>
  ) : null;
  const srRequired = field.required ? <span className="sr-only"> ({requiredMark})</span> : null;
  const errorText = error ? (
    <p id={errorId} className="mt-2 text-sm font-medium text-red-700">
      {error}
    </p>
  ) : null;

  if (field.kind === "single" && field.options) {
    return (
      <fieldset aria-describedby={error ? errorId : undefined}>
        <legend className="font-medium leading-relaxed text-brand-primary">
          {label}
          {marker}
          {srRequired}
        </legend>
        <div className="mt-4 space-y-3">
          {field.options.map((option, i) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-brand-primary/12 bg-brand-bg px-4 py-3 transition-colors hover:border-teal-strong has-[:checked]:border-teal-strong has-[:checked]:bg-teal-soft"
            >
              <input
                id={i === 0 ? id : undefined}
                type="radio"
                name={id}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="mt-1 size-4 shrink-0 accent-[#2f6b67]"
              />
              <span className="leading-relaxed text-ink-soft">{formText(option.label, locale)}</span>
            </label>
          ))}
        </div>
        {errorText}
      </fieldset>
    );
  }

  const common = {
    id,
    value,
    required: field.required,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? errorId : undefined,
    className: inputCls,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
  };

  return (
    <div>
      <label htmlFor={id} className="block font-medium leading-relaxed text-brand-primary">
        {label}
        {marker}
        {srRequired}
      </label>
      <div className="mt-3">
        {field.kind === "long" ? (
          <textarea {...common} rows={4} maxLength={5000} />
        ) : (
          <input
            {...common}
            type={field.inputType ?? "text"}
            autoComplete={field.autoComplete}
            maxLength={254}
          />
        )}
      </div>
      {errorText}
    </div>
  );
}
